<template>
  <div v-if="show" class="summary-panel">
    <h3>Raw Material Source Summary</h3>
    <div class="filter-info">
      <p v-if="startDate && endDate">
        <strong>Date Range:</strong> {{ formatDate(startDate) }} to {{ formatDate(endDate) }}
      </p>
      <p v-if="supplierName">
        <strong>Supplier:</strong> {{ supplierName }}
      </p>
      <p v-if="community">
        <strong>Community:</strong> {{ community }}
      </p>
      <p v-if="!startDate && !endDate && !supplierName && !community">
        <em>No filters applied - showing all data</em>
      </p>
    </div>
    
    <button @click="$emit('close')" class="close-btn">X</button>
    
    <div v-if="loading" class="loading">Loading...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else class="summary-stats">
      <div class="stat-item">
        <span class="stat-label">Total Tonnage:</span>
        <span class="stat-value">{{ totalTonnage }} tons</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Number of Purchases:</span>
        <span class="stat-value">{{ purchaseCount }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Number of Suppliers:</span>
        <span class="stat-value">{{ supplierCount }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-label">Average Price per Ton:</span>
        <span class="stat-value">₦{{ Number(averagePrice).toLocaleString() }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
    supplierName: {
      type: String,
      default: null,
    },
    community: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      totalTonnage: 0,
      supplierCount: 0,
      averagePrice: 0,
      purchaseCount: 0,
      error: null,
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.querySuppliers();
        }
      },
    },
    startDate: {
      immediate: false,
      handler(newVal, oldVal) {
        if (this.show && newVal !== oldVal) {
          this.querySuppliers();
        }
      },
    },
    endDate: {
      immediate: false,
      handler(newVal, oldVal) {
        if (this.show && newVal !== oldVal) {
          this.querySuppliers();
        }
      },
    },
    supplierName: {
      immediate: false,
      handler(newVal, oldVal) {
        if (this.show && newVal !== oldVal) {
          this.querySuppliers();
        }
      },
    },
    community: {
      immediate: false,
      handler(newVal, oldVal) {
        if (this.show && newVal !== oldVal) {
          this.querySuppliers();
        }
      },
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    async querySuppliers() {
      this.loading = true;
      this.error = null;
      try {
        const layers = this.$store.getters["arcgisMap/getMapLayers"];
        const rmSourceLayer = layers.find((layer) => layer.id === "rmSourceLayer");
        if (!rmSourceLayer) {
          throw new Error("rmSourceLayer not found in Vuex store");
        }

        await rmSourceLayer.when();
        const dateField = rmSourceLayer.fields.find((f) => f.name === "date");
        if (!dateField) {
          throw new Error("Date field not found in layer");
        }
        if (dateField.type !== "date" && dateField.type !== "string") {
          throw new Error("Date field must be a date or string type");
        }

        let whereClause = "1=1";

        // Date filter
        if (
          this.startDate &&
          this.endDate &&
          !isNaN(new Date(this.startDate).getTime()) &&
          !isNaN(new Date(this.endDate).getTime())
        ) {
          if (new Date(this.startDate) > new Date(this.endDate)) {
            throw new Error("End date must be after start date");
          }
          const start = new Date(this.startDate).toISOString().split("T")[0];
          const end = new Date(this.endDate).toISOString().split("T")[0];
          
          if (dateField.type === "date") {
            whereClause += ` AND date >= DATE '${start}' AND date <= DATE '${end}'`;
          } else {
            whereClause += ` AND date >= '${start}T00:00:00.000Z' AND date <= '${end}T23:59:59.999Z'`;
          }
        }

        // Supplier Name filter
        if (this.supplierName && this.supplierName.trim()) {
          whereClause += ` AND supplierName = '${this.supplierName.trim().replace(/'/g, "''")}'`;
        }

        // Community filter
        if (this.community && this.community.trim()) {
          whereClause += ` AND community = '${this.community.trim().replace(/'/g, "''")}'`;
        }

        const query = rmSourceLayer.createQuery();
        query.where = whereClause;
        query.outFields = ["suppliedTonnage", "pricePerTon", "supplierName", "community"];
        query.returnGeometry = false;

        const response = await rmSourceLayer.queryFeatures(query);

        if (response.features && response.features.length > 0) {
          // Calculate statistics manually
          let totalTonnage = 0;
          let totalPrice = 0;
          let validPriceCount = 0;
          const uniqueSuppliers = new Set();

          response.features.forEach(feature => {
            const attributes = feature.attributes;
            
            // Calculate total tonnage
            const tonnage = parseFloat(attributes.suppliedTonnage) || 0;
            totalTonnage += tonnage;
            
            // Track unique suppliers
            if (attributes.supplierName) {
              uniqueSuppliers.add(attributes.supplierName);
            }
            
            // Calculate average price
            const priceStr = attributes.pricePerTon || "";
            const price = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
            if (!isNaN(price)) {
              totalPrice += price;
              validPriceCount++;
            }
          });

          this.totalTonnage = totalTonnage.toFixed(2);
          this.purchaseCount = response.features.length;
          this.supplierCount = uniqueSuppliers.size;
          this.averagePrice = validPriceCount > 0 
            ? (totalPrice / validPriceCount).toFixed(2)
            : "0.00";
        } else {
          this.totalTonnage = "0.00";
          this.purchaseCount = 0;
          this.supplierCount = 0;
          this.averagePrice = "0.00";
        }
      } catch (error) {
        console.error("Error querying suppliers:", {
          message: error.message,
          details: error.details,
          httpStatus: error.details?.httpStatus,
          raw: error.details?.raw,
        });
        this.error = "Unable to load supplier data. Please try again.";
        this.supplierCount = 0;
        this.totalTonnage = "0.00";
        this.purchaseCount = 0;
        this.averagePrice = "0.00";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
  
<style scoped>
.summary-panel {
  position: absolute;
  bottom: 100px;
  left: 70px;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  z-index: 1000;
  font-family: Arial, sans-serif;
}

.summary-panel h3{
margin-bottom: 0.5rem;
}

.filter-info {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  border-left: 4px solid #4caf50;
}

.filter-info p {
  margin: 5px 0;
  font-size: 14px;
}

.filter-info em {
  color: #666;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #666;
}

.error {
  color: #f44336;
  margin: 10px 0;
  padding: 10px;
  background: #ffebee;
  border-radius: 4px;
  border-left: 4px solid #f44336;
}

.summary-stats {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-weight: 500;
  color: #333;
}

.stat-value {
  font-weight: bold;
  color: #4caf50;
  margin-left: 0.3rem;
}

.close-btn {
  position: absolute;
  top: 0px;
  right: 0px;
  margin: 0;
  padding: 5px 10px;
  /* background: #f44336; */
  color: #000;
  border: none;
  /* border-radius: 4px; */
  cursor: pointer;
  font-size: 12px;
  font-weight: bolder;
}

.close-btn:hover {
  font-size: 14px;
}

button:not(.close-btn) {
  margin-top: 15px;
  padding: 8px 15px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
}

button:not(.close-btn):hover {
  background: #3e8e41;
}
</style>